import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { X, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { functions } from '../../lib/firebase';

interface Props {
  companyId: string | undefined;
  open: boolean;
  onClose: () => void;
  onConnected?: (connectionId: string) => void;
}

interface PasteMondayTokenRequest {
  action: 'pasteMondayApiToken';
  companyId: string;
  apiToken: string;
  workspaceId: string;
}

interface PasteMondayTokenResponse {
  success: boolean;
  connectionId: string;
  message?: string;
}

/**
 * Fallback paste path for Monday.com when the OAuth popup flow is not
 * available (e.g. self-hosted Monday or restricted account). Mirrors the
 * UX of ConnectMyCrmModal and PasteMetaCredentialsModal: token field is
 * `type="password"`, optional workspace ID, single Connect button.
 *
 * Wires to the `connectMondayCredentials` callable (action='pasteMondayApiToken'):
 *   input  = { action: 'pasteMondayApiToken', companyId, apiToken, workspaceId }
 *   output = { success: true, connectionId }
 */
export const PasteMondayTokenModal: React.FC<Props> = ({
  companyId,
  open,
  onClose,
  onConnected,
}) => {
  const [apiToken, setApiToken] = useState('');
  const [workspaceId, setWorkspaceId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);

  if (!open) return null;

  const reset = () => {
    setApiToken('');
    setWorkspaceId('');
    setError(null);
    setConnected(false);
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!companyId) {
      setError('No company selected');
      return;
    }
    if (!apiToken.trim()) {
      setError('API token is required');
      return;
    }
    if (!workspaceId.trim()) {
      setError('Workspace ID is required');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const fn = httpsCallable<PasteMondayTokenRequest, PasteMondayTokenResponse>(
        functions,
        'connectMondayCredentials'
      );
      const result = await fn({
        action: 'pasteMondayApiToken',
        companyId,
        apiToken: apiToken.trim(),
        workspaceId: workspaceId.trim(),
      });
      setConnected(true);
      onConnected?.(result.data.connectionId);
      setTimeout(() => {
        reset();
        onClose();
      }, 1200);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded bg-[#6c5ce7] text-white inline-flex items-center justify-center font-bold text-sm">
              M
            </span>
            <h2 className="text-base font-semibold text-white">
              Paste Monday.com API token
            </h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-1.5 rounded hover:bg-gray-800 text-gray-400"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="rounded-md border border-blue-500/30 bg-blue-500/5 p-3 text-xs text-blue-300 flex gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              In Monday.com: click your avatar &rarr; Developers &rarr; My
              Access Tokens. Copy your personal API token. The optional
              workspace ID limits which workspace this connection covers.
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1">
              API token
            </label>
            <input
              type="password"
              value={apiToken}
              onChange={(e) => setApiToken(e.target.value)}
              placeholder="eyJhbGciOiJIUzI1NiJ9..."
              className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white font-mono text-xs"
              autoComplete="off"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1">
              Workspace ID (optional)
            </label>
            <input
              type="text"
              value={workspaceId}
              onChange={(e) => setWorkspaceId(e.target.value)}
              placeholder="e.g. 12345678"
              className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white font-mono text-xs"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Leave empty to allow board discovery across all workspaces this
              token can access.
            </p>
          </div>
          {error && (
            <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-300">
              {error}
            </div>
          )}
          {connected && (
            <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs text-emerald-300 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /> Monday.com token validated
              and stored. Run Discover Boards to pick which boards to sync.
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-700">
          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            className="px-4 py-2 rounded-md text-sm text-gray-300 hover:bg-gray-800 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || connected || !companyId}
            className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {saving ? 'Validating…' : 'Connect'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PasteMondayTokenModal;
